import React from 'react';
import I18n from 'i18n-js';
import { ISelectOption } from '@reverbdotcom/commons/src/components/form_group_with_select';
import { RCCheckbox, RCSelect } from '@reverbdotcom/cadence/components';
import { compact, chunk, camelCase } from 'lodash';

enum Day {
  Monday = 'monday', Tuesday = 'tuesday', Wednesday = 'wednesday', Thursday = 'thursday',
  Friday = 'friday', Saturday = 'saturday', Sunday = 'sunday',
}

export interface ICategoryExemption {
  id?: string;
  categoryId: string;
  _destroy?: boolean;
}

export interface IProps {
  timezone?: string;
  mondayCutoff?: string;
  tuesdayCutoff?: string;
  wednesdayCutoff?: string;
  thursdayCutoff?: string;
  fridayCutoff?: string;
  saturdayCutoff?: string;
  sundayCutoff?: string;
  disabled?: boolean;
  allTimezones: ISelectOption[];
  allCategories: ISelectOption[];
  categoryExemptions?: ICategoryExemption[];
}

const AVAILABLE_TIMES = Object.freeze([
  '12:00 AM', '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM', '5:00 AM', '6:00 AM', '7:00 AM',
  '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM',
  '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM',
]);

function getShopInputName(inputName) {
  return `shipping[shop][${inputName}]`;
}

function getHandlingTimeInputName(inputName) {
  return `shipping[shop][handling_time_attributes][${inputName}]`;
}

function getHandlingTimeCategoryExemptionInputName(index, inputName) {
  return `shipping[shop][handling_time_attributes][handling_time_category_exemptions_attributes][${index}][${inputName}]`;
}

function CutoffDayCheckbox({ day, isChecked, isSameDayShippingDisabled, updateIsChecked }) {
  return (
    <RCCheckbox
      id={`${day}CutoffCheckbox`}
      name={`${day}CutoffCheckbox`}
      label={I18n.t(`discovery.dashboard.selling.sameDayShipping.${day}`)}
      checked={isChecked}
      disabled={isSameDayShippingDisabled}
      onChange={() => { updateIsChecked(!isChecked); }}
    />
  );
}

function CutoffTimeDropdown({ inputName, isChecked, cutoffTime, isSameDayShippingDisabled, updateCutoffTime }) {
  return (
    <RCSelect
      id={inputName}
      name={inputName}
      label=""
      value={isChecked ? cutoffTime : ''}
      disabled={!isChecked || isSameDayShippingDisabled}
      addEmptyOption
      onChange={(evt) => {
        updateCutoffTime(evt.target.value);
      }}
    >
      {AVAILABLE_TIMES.map((timeOption) => (
        <RCSelect.Option
          key={timeOption}
          label={timeOption}
          value={timeOption}
        />
      ))}
    </RCSelect>
  );
}

function CutoffRow({ day, isChecked, cutoffTime, isSameDayShippingDisabled, updateIsChecked, updateCutoffTime }) {
  return (
    <div className="d-flex fx-align-center mb-1">
      <div className="width-50">
        <CutoffDayCheckbox
          day={day}
          isChecked={isChecked}
          isSameDayShippingDisabled={isSameDayShippingDisabled}
          updateIsChecked={updateIsChecked}
        />
      </div>
      <div className="width-50">
        <CutoffTimeDropdown
          inputName={`${day}Cutoff`}
          isChecked={isChecked}
          cutoffTime={cutoffTime}
          isSameDayShippingDisabled={isSameDayShippingDisabled}
          updateCutoffTime={updateCutoffTime}
        />
      </div>
      <input
        hidden
        readOnly
        value={isChecked ? cutoffTime : ''}
        name={getHandlingTimeInputName(`${day}_cutoff`)}
        id={getHandlingTimeInputName(`${day}_cutoff`)}
        type="hidden"
      />
    </div>
  );
}

function CategoryExemptionCheckboxes(props: {
  allCategories: ISelectOption[], categoryExemptions: ICategoryExemption[], setCategoryExemptions,
  allCategoriesAllowed: boolean, setAllCategoriesAllowed, allCategoriesDisabled: boolean, sameDayShippingDisabled: boolean
}) {
  return (
    <>
      <div className="d-flex mb-1">
        <div className="width-100">
          <RCCheckbox
            id="allCategoriesCheckbox"
            name="allCategoriesCheckbox"
            label={I18n.t('discovery.dashboard.selling.sameDayShipping.allCategories')}
            checked={props.allCategoriesAllowed}
            onChange={() => { props.setAllCategoriesAllowed(!props.allCategoriesAllowed); }}
            disabled={props.allCategoriesDisabled || props.sameDayShippingDisabled}
          />
        </div>
      </div>

      {props.allCategoriesAllowed ? null : <div className="d-flex mb-1">
        {chunk(props.allCategories, Math.ceil(props.allCategories.length / 3.0)).map((categories, idx) => (
          <div className="width-30" key={idx}>
            {categories.map((category) => (
              <div className="mb-1" key={category.value}>
                <RCCheckbox
                  id={`${camelCase(category.text)}Checkbox`}
                  name={`${camelCase(category.text)}Checkbox`}
                  label={category.text}
                  checked={!props.categoryExemptions.some(ce => ce.categoryId === category.value && !ce._destroy)}
                  disabled={props.allCategoriesAllowed || props.sameDayShippingDisabled}
                  onChange={() => {
                    let newCatExemptions;

                    if (props.categoryExemptions.some(ce => ce.categoryId === category.value)) {
                      newCatExemptions = compact(props.categoryExemptions.map((ce) => {
                        if (ce.categoryId !== category.value) {
                          return ce;
                        }
                        return (ce.id) ? { ...ce, _destroy: !ce._destroy } : null;
                      }));
                    } else {
                      newCatExemptions = [...props.categoryExemptions, { categoryId: category.value }];
                    }

                    props.setCategoryExemptions(newCatExemptions);
                  }}
                />
              </div>
            ))}
          </div>
        ))}
      </div>}

      <hr className="mt-3 mb-3" />

      {props.categoryExemptions.map((categoryExemption, idx) => (
        <React.Fragment key={categoryExemption.categoryId}>
          <input
            hidden
            readOnly
            value={categoryExemption.id}
            name={getHandlingTimeCategoryExemptionInputName(idx, 'id')}
            id={getHandlingTimeCategoryExemptionInputName(idx, 'id')}
            type="hidden"
          />
          <input
            hidden
            readOnly
            value={categoryExemption.categoryId}
            name={getHandlingTimeCategoryExemptionInputName(idx, 'category_id')}
            id={getHandlingTimeCategoryExemptionInputName(idx, 'category_id')}
            type="hidden"
          />
          <input
            hidden
            readOnly
            value={categoryExemption._destroy ? '1' : '0'}
            name={getHandlingTimeCategoryExemptionInputName(idx, '_destroy')}
            id={getHandlingTimeCategoryExemptionInputName(idx, '_destroy')}
            type="hidden"
          />
        </React.Fragment>
      ))}
    </>
  );
}

export function ShopSettingsSameDayShipping(props: IProps) {
  const [timezone, setTimezone] = React.useState(props.timezone || '');
  const [selectedDays, setSelectedDays] = React.useState<string[]>(compact([
    props.mondayCutoff ? Day.Monday : null,
    props.tuesdayCutoff ? Day.Tuesday : null,
    props.wednesdayCutoff ? Day.Wednesday : null,
    props.thursdayCutoff ? Day.Thursday : null,
    props.fridayCutoff ? Day.Friday : null,
    props.saturdayCutoff ? Day.Saturday : null,
    props.saturdayCutoff ? Day.Sunday : null,
  ]));
  const [cutoffs, setCutoffs] = React.useState({
    [Day.Monday]: props.mondayCutoff || '',
    [Day.Tuesday]: props.tuesdayCutoff || '',
    [Day.Wednesday]: props.wednesdayCutoff || '',
    [Day.Thursday]: props.thursdayCutoff || '',
    [Day.Friday]: props.fridayCutoff || '',
    [Day.Saturday]: props.saturdayCutoff || '',
    [Day.Sunday]: props.sundayCutoff || '',
  });
  const [sameDayShippingDisabled, setSameDayShippingDisabled] = React.useState(!!props.disabled || false);
  const [categoryExemptions, setCategoryExemptions] = React.useState<ICategoryExemption[]>(props.categoryExemptions || []);
  const [allCategoriesAllowed, setAllCategoriesAllowed] = React.useState((props.categoryExemptions || []).length === 0);

  function configurationExists() {
    return !!selectedDays.length && !!compact(Object.values(cutoffs)).length;
  }

  return (
    <>
      <div className="mb-2">
        <h4 className="size-110 weight-bold mb-2">
          {I18n.t('discovery.dashboard.selling.sameDayShipping.formDescription')}
        </h4>

        <RCSelect
          id={getShopInputName('timezone')}
          name={getShopInputName('timezone')}
          label={I18n.t('discovery.dashboard.selling.sameDayShipping.timeZone')}
          value={timezone}
          required={configurationExists()}
          addEmptyOption
          onChange={(evt) => {
            setTimezone(evt.target.value);
          }}
        >
          {props.allTimezones.map((timezoneOption) => (
            <RCSelect.Option
              key={timezoneOption.text}
              label={timezoneOption.text}
              value={timezoneOption.value}
            />
          ))}
        </RCSelect>
      </div>

      <div className="d-flex mb-1">
        <div className="width-50">
          <div className="form-group__header">
            <label>{I18n.t('discovery.dashboard.selling.sameDayShipping.daysDescription')}</label>
          </div>
        </div>
        <div className="width-50">
          <div className="form-group__header">
            <label>{I18n.t('discovery.dashboard.selling.sameDayShipping.timesDescription')}</label>
          </div>
        </div>
      </div>

      {Object.values(Day).map(
        (day) => {
          return (
            <CutoffRow
              key={day}
              day={day}
              isChecked={selectedDays.includes(day)}
              cutoffTime={cutoffs[day]}
              isSameDayShippingDisabled={sameDayShippingDisabled}
              updateIsChecked={() => {
                const newDaysWithCutoffs = selectedDays.includes(day) ?
                  selectedDays.filter((dayWithCutoff) => { return day !== dayWithCutoff; }) : [...selectedDays, day];
                setSelectedDays(newDaysWithCutoffs);
              }}
              updateCutoffTime={(cutoffTime) => {
                setCutoffs({ ...cutoffs, [day]: cutoffTime });
              }}
            />
          );
        },
      )}

      <div className="d-flex mb-1 mt-3">
        <div className="form-group__header">
          <label>{I18n.t('discovery.dashboard.selling.sameDayShipping.categoryExemptionDescription')}</label>
        </div>
      </div>

      <CategoryExemptionCheckboxes
        allCategories={props.allCategories}
        categoryExemptions={categoryExemptions}
        setCategoryExemptions={setCategoryExemptions}
        allCategoriesAllowed={allCategoriesAllowed}
        setAllCategoriesAllowed={setAllCategoriesAllowed}
        allCategoriesDisabled={categoryExemptions.filter((ce) => !ce._destroy).length > 0 || !configurationExists()}
        sameDayShippingDisabled={sameDayShippingDisabled}
      />

      <div className="d-flex mb-3">
        <RCCheckbox
          id="sameDayShippingDisabled"
          name="sameDayShippingDisabled"
          label={I18n.t('discovery.dashboard.selling.sameDayShipping.disabled.label')}
          sublabel={I18n.t('discovery.dashboard.selling.sameDayShipping.disabled.helpText')}
          checked={sameDayShippingDisabled}
          onChange={() => { setSameDayShippingDisabled(!sameDayShippingDisabled); }}
          disabled={!configurationExists()}
        />
        <input
          hidden
          readOnly
          value={sameDayShippingDisabled ? '1' : '0'}
          name={getHandlingTimeInputName('disabled')}
          id={getHandlingTimeInputName('disabled')}
          type="hidden"
        />
      </div>
    </>
  );
}

export default ShopSettingsSameDayShipping;
